import { register } from '|>/shared/vjs'
import { BaseMenu } from '../../../base'
import { LocalEvents } from '../../local-events'
import { SettingsBackTitle } from '../../settings-back-title'
import { SettingsCustomMenuItem } from './settings-custom-menu-item'
import { SettingsSubtitlesCustomMenuItem } from './settings-subtitles-custom-menu-item'

/**
 * Interface for any component that can trigger events
 */
interface IEventContainer {
  trigger(event: string, data?: any): void
}

/**
 * Settings subtitles custom menu main screen (list of options)
 */
@register
export class SettingsSubtitlesCustomMenuMain extends BaseMenu {
  declare container: IEventContainer | undefined

  // menu items
  declare backTitle: SettingsBackTitle
  declare fontFamilyItem: SettingsSubtitlesCustomMenuItem
  declare fontColorItem: SettingsSubtitlesCustomMenuItem
  declare fontSizeItem: SettingsSubtitlesCustomMenuItem
  declare fontOpacityItem: SettingsSubtitlesCustomMenuItem
  declare backgroundColorItem: SettingsSubtitlesCustomMenuItem
  declare backgroundOpacityItem: SettingsSubtitlesCustomMenuItem
  declare windowColorItem: SettingsSubtitlesCustomMenuItem
  declare windowOpacityItem: SettingsSubtitlesCustomMenuItem
  declare characterEdgeStyleItem: SettingsSubtitlesCustomMenuItem
  declare resetSettingsItem: SettingsCustomMenuItem

  update() {
    // Note: backTitle.container is set in parent SettingsSubtitlesCustomMenu
    // to point to SettingsMenuContainer for back navigation

    // Set container for all items (they need SettingsSubtitlesCustomMenu as container)
    this.fontFamilyItem.container = this.container
    this.fontColorItem.container = this.container
    this.fontSizeItem.container = this.container
    this.fontOpacityItem.container = this.container
    this.backgroundColorItem.container = this.container
    this.backgroundOpacityItem.container = this.container
    this.windowColorItem.container = this.container
    this.windowOpacityItem.container = this.container
    this.characterEdgeStyleItem.container = this.container

    // Update all items to show current selected values
    this.fontFamilyItem.update()
    this.fontColorItem.update()
    this.fontSizeItem.update()
    this.fontOpacityItem.update()
    this.backgroundColorItem.update()
    this.backgroundOpacityItem.update()
    this.windowColorItem.update()
    this.windowOpacityItem.update()
    this.characterEdgeStyleItem.update()
  }

  setResetHandler(handler: () => void) {
    this.resetSettingsItem.handleClick = handler
  }

  activate() {
    this.addClass('vjs-x-active')
    // Update all items to show current selected values
    this.update()
  }

  deactivate() {
    this.removeClass('vjs-x-active')
  }
}

SettingsSubtitlesCustomMenuMain.options = {
  className: 'vjs-x-custom-main-menu vjs-x-settings-main-menu vjs-menu',
  children: [
    SettingsBackTitle.as('backTitle', {
      backEvent: LocalEvents.SettingsActivateSubtitlesMenu,
      title: 'custom_setting',
    }),

    SettingsCustomMenuItem.as('resetSettingsItem', {
      value: 'reset_setting',
    }),

    // subtitles custom menu items
    SettingsSubtitlesCustomMenuItem.as('fontFamilyItem', {
      label: 'font_family',
      event: LocalEvents.SettingsActivateSubtitlesFontFamilyMenu,
      settingKey: 'fontFamily',
    }),
    SettingsSubtitlesCustomMenuItem.as('fontColorItem', {
      label: 'font_color',
      event: LocalEvents.SettingsActivateSubtitlesFontColorMenu,
      settingKey: 'fontColor',
      getDisplayValue: (value: string) => value + '_color', // 'red' → 'red_color'
    }),
    SettingsSubtitlesCustomMenuItem.as('fontSizeItem', {
      label: 'font_size',
      event: LocalEvents.SettingsActivateSubtitlesFontSizeMenu,
      settingKey: 'fontSize',
      isPercentage: true,
    }),
    SettingsSubtitlesCustomMenuItem.as('fontOpacityItem', {
      label: 'font_opacity',
      event: LocalEvents.SettingsActivateSubtitlesFontOpacityMenu,
      settingKey: 'fontOpacity',
      isPercentage: true,
    }),
    SettingsSubtitlesCustomMenuItem.as('backgroundColorItem', {
      label: 'background_color',
      event: LocalEvents.SettingsActivateSubtitlesBackgroundColorMenu,
      settingKey: 'backgroundColor',
      getDisplayValue: (value: string) => value + '_color', // 'red' → 'red_color'
    }),
    SettingsSubtitlesCustomMenuItem.as('backgroundOpacityItem', {
      label: 'background_opacity',
      event: LocalEvents.SettingsActivateSubtitlesBackgroundOpacityMenu,
      settingKey: 'backgroundOpacity',
      isPercentage: true,
    }),
    SettingsSubtitlesCustomMenuItem.as('windowColorItem', {
      label: 'window_color',
      event: LocalEvents.SettingsActivateSubtitlesWindowColorMenu,
      settingKey: 'windowColor',
      getDisplayValue: (value: string) => value + '_color', // 'red' → 'red_color'
    }),
    SettingsSubtitlesCustomMenuItem.as('windowOpacityItem', {
      label: 'window_opacity',
      event: LocalEvents.SettingsActivateSubtitlesWindowOpacityMenu,
      settingKey: 'windowOpacity',
      isPercentage: true,
    }),
    SettingsSubtitlesCustomMenuItem.as('characterEdgeStyleItem', {
      label: 'character_edge_style',
      event: LocalEvents.SettingsActivateSubtitlesCharacterEdgeStyleMenu,
      settingKey: 'characterEdgeStyle',
    }),
  ],
}
